import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const WhatsAppKPIPanel = () => {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [messageFilter, setMessageFilter] = useState('all');
  const [searchPhone, setSearchPhone] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      // WhatsApp bookings vs app bookings
      const { data: orders } = await supabase?.from('orders')?.select('booking_channel, status, total_amount, created_at');

      const waOrders = orders?.filter(o => o?.booking_channel === 'whatsapp') || [];
      const appOrders = orders?.filter(o => o?.booking_channel !== 'whatsapp') || [];

      const waCompleted = waOrders?.filter(o => o?.status === 'delivered');
      const waRevenue = waCompleted?.reduce((sum, o) => sum + parseFloat(o?.total_amount || 0), 0);

      // Sessions
      const { data: sessions } = await supabase?.from('whatsapp_sessions')?.select('status, current_step, created_at');

      const totalSessions = sessions?.length || 0;
      const confirmedSessions = sessions?.filter(s => s?.status === 'completed')?.length || 0;
      const activeSessionsData = sessions?.filter(s => s?.status === 'active') || [];
      const conversionRate = totalSessions > 0 ? ((confirmedSessions / totalSessions) * 100)?.toFixed(1) : '0.0';
      const completionRate = waOrders?.length > 0 ? ((waCompleted?.length / waOrders?.length) * 100)?.toFixed(1) : '0.0';

      setStats({
        waBookings: waOrders?.length,
        appBookings: appOrders?.length,
        totalBookings: orders?.length || 0,
        waRevenue,
        conversionRate,
        completionRate,
        activeSessions: activeSessionsData?.length,
        confirmedSessions,
        totalSessions,
      });
      setActiveSessions(activeSessionsData);
    } catch (err) {
      console.error('WhatsApp stats error:', err);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      let query = supabase?.from('whatsapp_messages')?.select('*')?.order('created_at', { ascending: false })?.limit(100);

      if (messageFilter !== 'all') {
        query = query?.eq('direction', messageFilter);
      }
      if (searchPhone) {
        query = query?.ilike('phone_number', `%${searchPhone}%`);
      }

      const { data } = await query;
      setMessages(data || []);
    } catch (err) {
      console.error('WhatsApp messages error:', err);
    }
  }, [messageFilter, searchPhone]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchMessages()]);
      setLoading(false);
    };
    load();
  }, [fetchStats, fetchMessages]);

  // Real-time subscription for new messages
  useEffect(() => {
    const channel = supabase?.channel('whatsapp_messages_realtime')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'whatsapp_messages' }, () => {
        fetchMessages();
        fetchStats();
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [fetchMessages, fetchStats]);

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts)?.toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' });
  };

  const getStepBadge = (step) => {
    const colors = {
      greeting: 'bg-blue-100 text-blue-700',
      pickup: 'bg-yellow-100 text-yellow-700',
      dropoff: 'bg-orange-100 text-orange-700',
      fare_estimate: 'bg-purple-100 text-purple-700',
      confirmation: 'bg-indigo-100 text-indigo-700',
      confirmed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return colors?.[step] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-xl border border-border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-border animate-pulse" />
          <div className="w-48 h-5 rounded bg-border animate-pulse" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)]?.map((_, i) => (
            <div key={i} className="h-20 rounded-lg bg-border animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <Icon name="MessageCircle" size={20} color="#16a34a" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-text-primary">WhatsApp Bookings</h3>
            <p className="text-xs text-text-secondary">Twilio WhatsApp Business Channel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {stats?.activeSessions || 0} Active
          </span>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {[
          { id: 'overview', label: 'KPIs', icon: 'BarChart2' },
          { id: 'sessions', label: 'Sessions', icon: 'Activity' },
          { id: 'messages', label: 'Message Log', icon: 'MessageSquare' },
        ]?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab?.id
                ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon name={tab?.icon} size={15} />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* KPIs Tab */}
      {activeTab === 'overview' && (
        <div className="p-5 space-y-5">
          {/* KPI Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-xs text-green-600 font-medium mb-1">WhatsApp Bookings</p>
              <p className="text-2xl font-bold text-green-700">{stats?.waBookings || 0}</p>
              <p className="text-xs text-green-600 mt-1">vs {stats?.appBookings || 0} app bookings</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-xs text-blue-600 font-medium mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-blue-700">{stats?.conversionRate || '0.0'}%</p>
              <p className="text-xs text-blue-600 mt-1">{stats?.confirmedSessions} / {stats?.totalSessions} sessions</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-xs text-purple-600 font-medium mb-1">Completion Rate</p>
              <p className="text-2xl font-bold text-purple-700">{stats?.completionRate || '0.0'}%</p>
              <p className="text-xs text-purple-600 mt-1">of confirmed bookings</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4">
              <p className="text-xs text-yellow-600 font-medium mb-1">WA Revenue</p>
              <p className="text-2xl font-bold text-yellow-700">KES {(stats?.waRevenue || 0)?.toLocaleString()}</p>
              <p className="text-xs text-yellow-600 mt-1">from completed trips</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-4">
              <p className="text-xs text-orange-600 font-medium mb-1">Active Sessions</p>
              <p className="text-2xl font-bold text-orange-700">{stats?.activeSessions || 0}</p>
              <p className="text-xs text-orange-600 mt-1">in progress now</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-text-secondary font-medium mb-1">Channel Share</p>
              <p className="text-2xl font-bold text-text-primary">
                {stats?.totalBookings > 0
                  ? ((stats?.waBookings / stats?.totalBookings) * 100)?.toFixed(1)
                  : '0.0'}%
              </p>
              <p className="text-xs text-text-secondary mt-1">of all bookings</p>
            </div>
          </div>

          {/* Channel Comparison Bar */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-text-primary">Booking Channel Distribution</p>
            <div className="flex rounded-full overflow-hidden h-3 bg-border">
              {stats?.totalBookings > 0 && (
                <>
                  <div
                    className="bg-green-500 transition-all duration-500"
                    style={{ width: `${(stats?.waBookings / stats?.totalBookings) * 100}%` }}
                    title={`WhatsApp: ${stats?.waBookings}`}
                  />
                  <div
                    className="bg-primary transition-all duration-500"
                    style={{ width: `${(stats?.appBookings / stats?.totalBookings) * 100}%` }}
                    title={`App: ${stats?.appBookings}`}
                  />
                </>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-text-secondary">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> WhatsApp ({stats?.waBookings})</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary" /> App ({stats?.appBookings})</span>
            </div>
          </div>
        </div>
      )}
      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="p-5">
          {activeSessions?.length === 0 ? (
            <div className="text-center py-8 text-text-secondary">
              <Icon name="MessageCircle" size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No active sessions</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeSessions?.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Icon name="Phone" size={14} color="#16a34a" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">Active Session</p>
                      <p className="text-xs text-text-secondary">{formatTime(s?.created_at)}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStepBadge(s?.current_step)}`}>
                    {s?.current_step}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Message Log Tab */}
      {activeTab === 'messages' && (
        <div className="p-5 space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Search by phone..."
              value={searchPhone}
              onChange={e => setSearchPhone(e?.target?.value)}
              className="input-field text-sm flex-1"
            />
            <select
              value={messageFilter}
              onChange={e => setMessageFilter(e?.target?.value)}
              className="input-field text-sm w-full sm:w-36"
            >
              <option value="all">All Messages</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>

          {/* Message List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {messages?.length === 0 ? (
              <div className="text-center py-8 text-text-secondary">
                <Icon name="MessageSquare" size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No messages found</p>
              </div>
            ) : (
              messages?.map((msg) => (
                <div
                  key={msg?.id}
                  className={`p-3 rounded-lg border text-sm ${
                    msg?.direction === 'inbound' ?'bg-blue-50 border-blue-100' :'bg-green-50 border-green-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Icon
                        name={msg?.direction === 'inbound' ? 'ArrowDownLeft' : 'ArrowUpRight'}
                        size={13}
                        color={msg?.direction === 'inbound' ? '#2563eb' : '#16a34a'}
                      />
                      <span className="font-medium text-text-primary text-xs">{msg?.phone_number}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        msg?.direction === 'inbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {msg?.direction}
                      </span>
                      {msg?.message_type !== 'text' && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                          {msg?.message_type}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-text-secondary">{formatTime(msg?.created_at)}</span>
                  </div>
                  <p className="text-text-primary text-xs whitespace-pre-wrap line-clamp-3">{msg?.body}</p>
                  {msg?.twilio_sid && (
                    <p className="text-xs text-text-secondary mt-1 font-mono">SID: {msg?.twilio_sid}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppKPIPanel;
